import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { PageHeader, SectionCard, EmptyState } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtEUR } from "@/lib/format";
import { Plus, Warehouse, AlertTriangle, Search } from "lucide-react";

export default function WawiStock() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const write = can(user?.role, "wawi:write");
  const [wh, setWh] = useState("all");
  const [onlyLow, setOnlyLow] = useState(false);
  const [q, setQ] = useState("");
  const [adjust, setAdjust] = useState(null);

  const { data: whs } = useQuery({ queryKey: ["wawi-warehouses"], queryFn: async () => (await api.get("/wawi/warehouses")).data });
  const { data: arts } = useQuery({ queryKey: ["wawi-articles-all"], queryFn: async () => (await api.get("/wawi/articles?limit=200")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["wawi-stock", wh, onlyLow, q],
    queryFn: async () => (await api.get(`/wawi/stock?warehouse_id=${wh === "all" ? "" : wh}&low=${onlyLow}&q=${encodeURIComponent(q)}`)).data,
  });
  const rows = data?.items || [];
  const warehouses = whs?.items || [];

  const submit = async () => {
    const a = adjust;
    if (!a.article_id || !a.warehouse_id) return toast.error("Artikel und Lager wählen");
    const payload = { article_id: a.article_id, variant_id: a.variant_id || "", warehouse_id: a.warehouse_id, reason: a.reason || "" };
    if (a.mode === "set") payload.set_quantity = Number(a.amount) || 0;
    else payload.delta = (a.mode === "remove" ? -1 : 1) * (Number(a.amount) || 0);
    try { await api.post("/wawi/stock/adjust", payload); toast.success("Bestand aktualisiert"); setAdjust(null); qc.invalidateQueries({ queryKey: ["wawi-stock"] }); }
    catch (err) { toast.error(apiError(err)); }
  };

  const selectedArticle = (arts?.items || []).find((x) => x.id === adjust?.article_id);

  return (
    <div>
      <PageHeader title="Lager" description="Mengenbasierter Bestand (Zubehör & Standardartikel)"
        actions={write && <Button className="gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => setAdjust({ mode: "add", amount: 1, warehouse_id: warehouses[0]?.id || "" })} data-testid="stock-adjust-button"><Plus className="h-4 w-4" />Bestand anpassen</Button>} />

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Artikel, Nr., EAN…" className="pl-9" data-testid="stock-search" />
        </div>
        <Select value={wh} onValueChange={setWh}>
          <SelectTrigger className="w-44" data-testid="stock-warehouse-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle Lager</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant={onlyLow ? "default" : "outline"} className={onlyLow ? "bg-amber-500 text-white hover:bg-amber-600" : ""} onClick={() => setOnlyLow((v) => !v)} data-testid="stock-low-filter">
          <AlertTriangle className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Mindestbestand</span>
        </Button>
      </div>

      <SectionCard title={`${rows.length} Bestandspositionen`}>
        {isLoading ? null : rows.length === 0 ? (
          <EmptyState icon={Warehouse} title="Kein Bestand" description="Buchen Sie Zubehör über „Bestand anpassen“ oder den Wareneingang ein." />
        ) : (
          <div className="space-y-2">
            {rows.map((s) => (
              <div key={s.id} className={`flex items-center justify-between gap-3 rounded-md border p-3 ${s.low ? "border-amber-500/40 bg-amber-500/5" : ""}`} data-testid={`stock-row-${s.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-medium">{s.article_name}{s.variant_name ? ` · ${s.variant_name}` : ""}</span>
                    {s.category && <Badge variant="outline" className="text-[10px]">{s.category}</Badge>}
                    {s.low && <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-300 text-[10px]">Unter Mindestbestand</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{s.article_number} · {s.warehouse_name}</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <div className="text-right">
                    <div className="text-lg font-semibold">{s.quantity}</div>
                    <div className="text-[10px] text-muted-foreground">Min: {s.min_stock || 0} · {fmtEUR(s.sale_price)}</div>
                  </div>
                  {write && <Button size="sm" variant="outline" onClick={() => setAdjust({ mode: "add", amount: 1, article_id: s.article_id, variant_id: s.variant_id, warehouse_id: s.warehouse_id })} data-testid={`stock-row-adjust-${s.id}`}>Anpassen</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Bestand anpassen</DialogTitle></DialogHeader>
          {adjust && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Artikel</Label>
                <Select value={adjust.article_id || ""} onValueChange={(v) => setAdjust({ ...adjust, article_id: v, variant_id: "" })}>
                  <SelectTrigger data-testid="adjust-article"><SelectValue placeholder="Artikel wählen" /></SelectTrigger>
                  <SelectContent>{(arts?.items || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select></div>
              {(selectedArticle?.variants || []).length > 0 && (
                <div className="space-y-1.5"><Label>Variante</Label>
                  <Select value={adjust.variant_id || "none"} onValueChange={(v) => setAdjust({ ...adjust, variant_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Keine" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Keine</SelectItem>{selectedArticle.variants.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              <div className="space-y-1.5"><Label>Lager</Label>
                <Select value={adjust.warehouse_id || ""} onValueChange={(v) => setAdjust({ ...adjust, warehouse_id: v })}>
                  <SelectTrigger data-testid="adjust-warehouse"><SelectValue placeholder="Lager wählen" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Art</Label>
                  <Select value={adjust.mode} onValueChange={(v) => setAdjust({ ...adjust, mode: v })}>
                    <SelectTrigger data-testid="adjust-mode"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="add">Zugang (+)</SelectItem><SelectItem value="remove">Abgang (−)</SelectItem><SelectItem value="set">Setzen (=)</SelectItem></SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Menge</Label><Input type="number" min="0" value={adjust.amount} onChange={(e) => setAdjust({ ...adjust, amount: e.target.value })} data-testid="adjust-amount" /></div>
              </div>
              <div className="space-y-1.5"><Label>Grund (optional)</Label><Input value={adjust.reason || ""} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} placeholder="z. B. Inventurkorrektur" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjust(null)}>Abbrechen</Button>
            <Button onClick={submit} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="adjust-save">Buchen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
